<script setup lang="ts">
import type { PreSignedFile, RecordingBlob } from '~/models/Media'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { faPause, faPlay, faStop } from '@fortawesome/pro-solid-svg-icons'
import { faArrowDownToLine, faMicrophone, faSpinner, faTrash } from '@fortawesome/duotone-regular-svg-icons'
import { AuToast } from '~/stores/toasts'
import { PendoFeature } from '~/models/Pendo'
import useFileUpload from '~/composables/useFileUpload'
import useAudioRecorder from '~/composables/useAudioRecorder'
import { useAppColor } from '~/composables/useAppColor'
import { useFileUploadApi } from '~/api/fileUploadApi'
import Tooltip from '~/components/ui/Tooltip.vue'
import Button from '~/components/ui/Button.vue'
import AuCallout from '~/components/ui/AuCallout.vue'
import AudioPlayer from '~/components/media/AudioPlayer.vue'

const props = defineProps<{
  fileName: string
  fileKey?: string
  mimeType?: string
  canEdit?: boolean
  time?: string
  duration?: number
}>()

const emits = defineEmits<{
  saveRecording: [recording: PreSignedFile]
  cancelRecording: []
}>()

const { t } = useI18n()
const { createPreSignedFile } = useFileUploadApi()
const { themeColor } = useAppColor()
const {
  start,
  pause,
  stop,
  formatSeconds,
  elapsedTime,
  resetRecording,
  isRecording,
  recorderOptions,
} = useAudioRecorder()

const {
  isUploading,
  isDownloading,
  fileTooLarge,
  postUploadFile,
  downloadFileKey,
  MAX_FILE_SIZE_MB,
} = useFileUpload()

const audioRecording = ref<RecordingBlob>()
const src = ref<string>()

const isRecordingActive = ref(false)
const isLoading = ref(false)
const durationFormated = computed(() => props.duration ? formatSeconds(props.duration) : props.time)

const errorMessage = ref<Nullable<{
  type: 'warning' | 'error'
  message: string
}>>(null)

const hasRecording = computed(() => audioRecording.value || props.fileKey)
const canDownload = computed(() => hasRecording.value && !isUploading.value && !isDownloading.value && !isRecordingActive.value)
const canRemove = computed(() => hasRecording.value && props.canEdit && !isUploading.value && !isDownloading.value && !isRecordingActive.value)

function startAudioRecording() {
  src.value = undefined

  start()
    .then(() => {
      errorMessage.value = null
      isRecordingActive.value = true
      fileTooLarge.value = false
    })
    .catch((error) => {
      console.error(error)
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        errorMessage.value = { type: 'error', message: t('recordError.notSupported') }
        return
      }
      switch (error.name) {
        case 'AbortError':
          errorMessage.value = { type: 'warning', message: t('recordError.aborted') }
          break
        case 'NotAllowedError':
          errorMessage.value = { type: 'error', message: t('recordError.notAllowed') }
          break
        case 'NotFoundError':
          errorMessage.value = { type: 'error', message: t('recordError.notFound') }
          break
        case 'NotReadableError':
          errorMessage.value = { type: 'error', message: t('recordError.notReadable') }
          break
        case 'SecurityError':
          errorMessage.value = { type: 'error', message: t('recordError.security') }
          break
        case 'TypeError':
          errorMessage.value = { type: 'error', message: t('recordError.type') }
          break
        case 'InvalidStateError':
          errorMessage.value = { type: 'error', message: t('recordError.state') }
          break
        default:
          errorMessage.value = { type: 'error', message: t('recordError.unknown') }
      }
    })
}

function stopAudioRecording() {
  stop()
    .then(({ blob, duration }) => {
      isRecordingActive.value = false
      audioRecording.value = {
        duration,
        blob,
        mimeType: blob.type.includes(';')
          ? blob.type.slice(0, blob.type.indexOf(';'))
          : blob.type
      }
      uploadRecording(audioRecording.value)
    })
    .catch((error) => {
      console.error(error)
      switch (error.name) {
        case 'InvalidStateError':
          errorMessage.value = { type: 'error', message: t('recordError.state') }
          break
        default:
          errorMessage.value = { type: 'error', message: t('recordError.unknown') }
      }
    })
}

function toggleFileKey(fileKey: string) {
  playFileKey(fileKey)
}

function playFileKey(fileKey: string) {
  isLoading.value = true
  createPreSignedFile(fileKey)
    .then((response) => {
      src.value = response.uri
    })
    .finally(() => isLoading.value = false)
}

function removeRecording() {
  if (!props.canEdit) {
    AuToast.error(t('media.errorNotAllowed'))
    return
  }
  resetRecording()
  isRecordingActive.value = false
  audioRecording.value = undefined
  emits('cancelRecording')
}

async function uploadRecording(recording: RecordingBlob) {
  const file = new File([recording.blob], `${props.fileName}.${recorderOptions.extension}`, { type: recording.mimeType })
  const response: PreSignedFile = await postUploadFile(file)
    .then(response => ({
      ...response,
      name: t('media.recordAudio'),
      duration: recording.duration
    }))
    .catch((error) => {
      AuToast.error(t('media.errorRecording'))
      throw error
    })
  emits('saveRecording', response)
}

async function downloadFile() {
  if (!props.fileKey) {
    AuToast.error(t('media.errorNotFound'))
    return
  }
  await downloadFileKey(props.fileKey)
    .catch((error) => {
      AuToast.error(t('media.downloadError'))
      throw error
    })
}

function onPlay(playFn: () => void) {
  if (!props.fileKey)
    return

  toggleFileKey(props.fileKey)
  playFn()
}
</script>

<template>
  <div
    class="rounded-xl bg-(--theme-5) p-4"
    :class="isRecordingActive || isDownloading || fileKey ? 'border-(--theme-20)' : 'border-(--theme-5)'"
  >
    <AuCallout
      v-if="errorMessage"
      class="mb-6"
      :variant="errorMessage.type"
      :title="errorMessage.message"
    />
    <AuCallout
      v-if="fileTooLarge"
      variant="error"
      class="mb-6"
      :title="t('media.fileIsTooLargeText', { mb: MAX_FILE_SIZE_MB })"
    />
    <AudioPlayer
      variant="big-button-no-text"
      :src
      autoplay
      load="visible"
      :style="{
        '--media-color': 'var(--theme-10)',
        '--media-bg-color': 'var(--theme-60)',
      }"
    >
      <template #trigger="{ isPlaying, activate, close, pause }">
        <div class="flex flex-col items-center justify-start gap-4 sm:flex-row sm:justify-between">
          <button
            v-if="isUploading"
            class="disabled flex grow items-center gap-4 rounded-xl px-5 py-3 transition-colors hover:bg-(--theme-10) focus-visible:ring"
            :disabled="true"
          >
            <div class="flex size-[4em] items-center justify-center rounded-full bg-(--theme-10) text-(--theme-50)">
              <font-awesome-icon
                :icon="faSpinner"
                :spin="isUploading"
                :class="{ 'ml-1.5': !isLoading }"
                size="2xl"
              />
            </div>
            <span
              class="font-semibold"
              v-text="t('formWriter.uploading')"
            />
          </button>
          <button
            v-if="fileKey && !isRecordingActive"
            class="group flex grow items-center gap-4 rounded-xl px-5 py-3 transition-colors hover:bg-(--theme-10) focus-visible:ring"
            :disabled="isLoading"
            :data-pendo="isPlaying ? PendoFeature.AudioRecorder.StopPlay : PendoFeature.AudioRecorder.PlayButton"
            @click="isPlaying ? close() : onPlay(activate)"
          >
            <div class="flex size-[4em] items-center justify-center rounded-full bg-(--theme-10) text-(--theme-50) group-hover:bg-(--theme-5)">
              <font-awesome-icon
                :icon="isLoading ? faSpinner : isPlaying ? faStop : faPlay"
                :spin="isLoading"
                :class="{ 'ml-1.5': !isLoading && !isPlaying }"
                size="2xl"
              />
            </div>
            <div v-if="durationFormated" class="flex flex-col items-start gap-1.5">
              <span
                class="text-[1em] font-bold"
                v-text="isPlaying ? t('media.stopPlayingRecording') : t('media.playRecording')"
              />
              <div
                v-if="!isPlaying"
                class="font-digital text-2xl"
                v-text="durationFormated"
              />
            </div>
            <span
              v-else
              class="font-semibold"
              v-text="t('media.playRecording')"
            />
          </button>
          <button
            v-if="!audioRecording && !isRecordingActive && !fileKey"
            class="group flex grow items-center gap-4 rounded-xl px-5 py-3 transition-colors hover:bg-(--theme-10) focus-visible:ring"
            :class="{ disabled: isUploading || !canEdit }"
            :disabled="isUploading || !canEdit"
            :data-pendo="PendoFeature.AudioRecorder.StartButton"
            @click="() => { activate(); startAudioRecording() }"
          >
            <div class="flex size-[4em] items-center justify-center rounded-full bg-(--theme-10) text-(--theme-50) group-hover:bg-(--theme-5)">
              <font-awesome-icon
                :icon="faMicrophone"
                size="2xl"
              />
            </div>
            <div class="flex flex-col items-start gap-1.5">
              <span
                class="text-[1em] font-bold"
                v-text="t('media.startRecording')"
              />
              <div
                class="font-digital text-2xl"
                v-text="formatSeconds(elapsedTime)"
              />
            </div>
          </button>
          <button
            v-if="isRecordingActive"
            class="group flex grow items-center gap-4 rounded-xl px-5 py-3 transition-colors hover:bg-(--theme-10) focus-visible:ring"
            :disabled="isUploading"
            :data-pendo="PendoFeature.AudioRecorder.StopButton"
            @click="() => { close(); stopAudioRecording() }"
          >
            <div class="flex size-[4em] items-center justify-center rounded-full bg-(--theme-10) text-(--theme-50) group-hover:bg-(--theme-5)">
              <font-awesome-icon
                :icon="faStop"
                size="2xl"
              />
            </div>
            <div class="flex flex-col items-start gap-1.5">
              <span
                class="text-[1em] font-bold"
                v-text="t('media.stopRecording')"
              />
              <div
                class="font-digital text-2xl"
                v-text="formatSeconds(elapsedTime)"
              />
            </div>
          </button>
          <div v-if="canRemove || canDownload" class="flex items-center gap-2">
            <Tooltip :content="t('media.download')">
              <Button
                shape="square"
                variant="outline"
                :disabled="!canDownload"
                :color="themeColor"
                size="lg"
                :aria-label="t('media.download')"
                :data-pendo="PendoFeature.AudioRecorder.DownloadButton"
                @click="downloadFile"
              >
                <font-awesome-icon
                  :icon="isDownloading ? faSpinner : faArrowDownToLine"
                  :spin="isDownloading"
                  :class="{ disabled: !canDownload }"
                />
              </Button>
            </Tooltip>
            <Tooltip :content="t('media.removeRecording')">
              <Button
                shape="square"
                variant="outline"
                :color="themeColor"
                size="lg"
                :disabled="!canRemove"
                :aria-label="t('media.removeRecording')"
                :data-pendo="PendoFeature.AudioRecorder.RemoveButton"
                @click="removeRecording"
              >
                <font-awesome-icon
                  :icon="faTrash"
                  :class="{ disabled: !canRemove }"
                />
              </Button>
            </Tooltip>
          </div>
        </div>
      </template>

      <template v-if="isRecordingActive" #default="{ close }">
        <div class="flex w-full items-center justify-center gap-4">
          <Button
            v-show="!isRecording"
            :disabled="isUploading"
            :color="themeColor"
            shade="light"
            size="xl"
            :data-pendo="PendoFeature.AudioRecorder.ResumeButton"
            @click="() => start()"
          >
            <font-awesome-icon
              :icon="faPlay"
              size="lg"
            />
            {{ t('media.continueRecording') }}
          </Button>
          <Button
            v-show="isRecording"
            :disabled="isUploading"
            :data-pendo="PendoFeature.AudioRecorder.PauseButton"
            :color="themeColor"
            shade="light"
            size="xl"
            @click="() => pause()"
          >
            <font-awesome-icon
              :icon="faPause"
              size="lg"
            />
            {{ t('media.pauseRecording') }}
          </Button>
          <Button
            :disabled="isUploading"
            :data-pendo="PendoFeature.AudioRecorder.StopButton"
            :color="themeColor"
            shade="light"
            size="xl"
            @click="() => { close(); stopAudioRecording() }"
          >
            <font-awesome-icon
              :icon="faStop"
              size="lg"
            />
            {{ t('media.stopRecording') }}
          </Button>
          <p
            class="font-digital text-2xl"
            v-text="formatSeconds(elapsedTime)"
          />
        </div>
      </template>
    </AudioPlayer>
  </div>
</template>

<style scoped>
.disabled {
  opacity: 0.7;
  cursor: not-allowed;
  filter: grayscale(100%);
}
</style>
